import { defineStore } from "pinia";
import Router from '../routes';
import Http from '../utils/http';
import Utils from '../utils/utils';
import Team from '../entities/Team';
import i18n from "../localizations";
import TeamNameGenerator from '../services/TeamNameGenerator';
import { useRootStore } from "./root";

export const useGamesStore = defineStore("games", {
    state: () => ({
        lastCreatedGame: null,
        divisions: [],
        lecturerUsers: [],
        teamCount: 10,
        pickedUser: null,
        teams: [],
        nextTeamId: 1,
        teamNameGenerator: new TeamNameGenerator()
    }),
    
    getters: {
        findPickedLecturerUser: (state) => () => {
            return state.pickedUser ? state.lecturerUsers.find(u => u.id === state.pickedUser.id) : null;
        },

        findLecturerUser: (state) => (userId) => {
            return state.lecturerUsers.find(u => u.id === userId);
        },
    },
    
    actions: {
        setDivisions(divisions) {
            this.divisions = [];

            divisions.forEach(division => this.divisions.push({
                id: division['id'],
                key: division['key']
            }));
        },

        setLecturerUsers(users) {
            this.lecturerUsers = [];

            users.forEach(user => this.lecturerUsers.push({
                id: user['id'],
                fullName: user['full_name'],
                online: false,
                teamId: null
            }));
        },

        setTeamCount(teamCount) {
            this.teamCount = teamCount;
        },

        setPickedUser(user) {
            this.pickedUser = user;
        },

        clearPickedUsers() {
            this.lecturerUsers.forEach(user => user.teamId = null);
        },

        addTeam() {
            const teamNames = this.teams.map(t => t.name);

            let name;
            do {
                name = this.teamNameGenerator.getName();
            } while (teamNames.includes(name));
            
            this.teams.push(new Team(this.nextTeamId++, name, this.divisions.map(d => d.key)));
        },
        
        removeUserFromTeam(user) {
            if (user.teamId !== null) {
                const teamIndex = this.teams.findIndex(t => t.id === user.teamId);
                
                if (teamIndex >= 0) {
                    const team = this.teams[teamIndex];

                    for (let [divisionKey, divisionUser] of Object.entries(team.divisions)) {
                        if (divisionUser !== null && divisionUser.id === user.id) {
                            team.removeUser(divisionKey);
                        }
                    }
                }
            }
        },

        createTeamsFromRandomTeamsData(randomTeams) {
            randomTeams.forEach((teamDivisions) => {
                const teamId = this.nextTeamId++;
                const newTeam = new Team(teamId, this.teamNameGenerator.getName(), this.divisions.map(d => d.key))

                Object.keys(teamDivisions).forEach((divisionKey) => {
                    const user = teamDivisions[divisionKey];
                    const foundUser = this.lecturerUsers.find(u => u.id === user.id);
    
                    if (foundUser) {
                        foundUser.teamId = teamId;
                    }

                    newTeam.assignUser(divisionKey, {
                        id: user.id,
                        fullName: user['full_name']
                    });
                })
                
                this.teams.push(newTeam);
            });
        },

        clearTeams() {
            if (this.teams.length) {
                this.lecturerUsers.forEach(user => user.teamId = null);
            }
            
            this.teams = [];
            this.nextTeamId = 1;
            this.teamNameGenerator = new TeamNameGenerator();
        },
        
        loadDivisions(templateId) {
            Http.get(`/api/templates/${templateId}/divisions`)
                .then(response => {
                    const divisions = response.data || [];
                    this.setDivisions(divisions);
                })
        },

        loadLecturerUsers() {
            const rootStore = useRootStore();
            
            Http.get(`/api/lecturer-users`)
                .then(response => {
                    this.setLecturerUsers(response.data || []);
                    
                    rootStore.websocket.join(`Classroom.${rootStore.user.id}`)
                        .here(users => {
                            users.forEach(user => {
                                const foundUser = this.lecturerUsers.find(lecturerUser => lecturerUser.id === user.id);
                                foundUser && (foundUser.online = true);
                            })
                        })
                        .listen("NewPlayerJoined", user => {
                            const foundIndex = this.lecturerUsers.findIndex(lecturerUser => lecturerUser.id === user.id);
                            
                            if (foundIndex === -1) {
                                this.lecturerUsers.push({
                                    id: user['id'],
                                    fullName: user['full_name'],
                                    online: false,
                                    teamId: null
                                });
                            }
                        })
                        .listen("PlayerLeft", user => {
                            const foundIndex = this.lecturerUsers.findIndex(lecturerUser => lecturerUser.id === user.id);
                            const foundUser = this.lecturerUsers[foundIndex];
                            
                            this.removeUserFromTeam(foundUser);
                            
                            if (foundIndex !== -1) {
                                this.lecturerUsers.splice(foundIndex, 1);
                            }
                        })
                        .joining(user => {
                            const foundUser = this.lecturerUsers.find(lecturerUser => lecturerUser.id === user.id);
                            foundUser && (foundUser.online = true);
                        })
                        .leaving(user => {
                            const foundUser = this.lecturerUsers.find(lecturerUser => lecturerUser.id === user.id);
                            
                            if (foundUser) {
                                this.removeUserFromTeam(foundUser);
                                foundUser.online = false;
                            }
                        });
                });
        },

        async getRandomTeams(templateId) {
            try {
                const { data } = await Http.get(`/api/templates/${templateId}/random-teams`);
                
                this.clearTeams()
                this.clearPickedUsers()
                this.createTeamsFromRandomTeamsData(data || []);
            } catch (error) {
                console.error(error)
            }
        },

        async createGame(payload) {
            const rootStore = useRootStore();
            
            try {
                await Http.post('/api/games', payload);
                
                const notification = Utils.createNotification(
                    'success',
                    i18n.t('notifications.game_successfully_created')
                );

                rootStore.clearNotifications();
                rootStore.pushNotification(notification);
                
                Router.push({ name: 'dashboard' }, null);
            } catch (error) {
                Utils.resolveValidationErrors(error);
                    
                this.teams.forEach((t, index) => {
                    t.setErrorIndex(index);
                });
            }
        },

        removeTeam(teamId) {
            const teamIndex = this.teams.findIndex(t => t.id === teamId);

            if (teamIndex < 0) {
                throw `Team with id ${teamId} does not exist!`;
            }

            const team = this.teams[teamIndex];
            const users = Object.keys(team.divisions)
                .map(keyDivision => team.divisions[keyDivision])
                .filter(user => user);

            users.forEach(user => {
                const foundUser = this.lecturerUsers.find(u => u.id === user.id);

                if (foundUser)
                    foundUser.teamId = null;
            });

            if (this.teams.length === 1) {
                this.nextTeamId = 1;
                this.teamNameGenerator = new TeamNameGenerator();
            } else if (teamIndex === this.teams.length - 1) {
                this.teamNameGenerator.goToPreviousName();
            }

            this.teams.splice(teamIndex, 1);
        },
        
        clearState() {
            const { websocket, user } = useRootStore();
            websocket && websocket.leave(`Classroom.${user.id}`)
            
            this.$reset();
        },
    }
})